import moment, { Moment } from 'moment';

export interface ShiftFormDateLimitsOptions {
	weekStartDate?: string | Date | null;
	isEdit?: boolean;
	existingStart?: string | Date | null;
	existingEnd?: string | Date | null;
	today?: Moment | Date | string;
}

export interface ShiftFormDateLimits {
	minDate?: string;
	maxDate?: string;
}

function toStartOfDay(value?: string | Date | null): Moment | null {
	if (!value) return null;
	const parsed = moment(value);
	return parsed.isValid() ? parsed.startOf('day') : null;
}

/**
 * Date bounds for the full-edit DateTimePickers.
 * Create still blocks days before today; edit allows the rest of the current
 * week (and the shift's existing dates) so opening an earlier-in-week shift
 * does not show minDate validation errors.
 */
export function getShiftFormDateLimits({
	weekStartDate,
	isEdit = false,
	existingStart,
	existingEnd,
	today: todayArg,
}: ShiftFormDateLimitsOptions): ShiftFormDateLimits {
	if (!weekStartDate) {
		return {};
	}

	const today = moment(todayArg ?? undefined).startOf('day');
	const weekStart = moment(weekStartDate).startOf('day');
	const weekEnd = weekStart.clone().add(6, 'days').endOf('day');

	let minDate = isEdit ? weekStart.clone() : moment.max(today.clone(), weekStart.clone());
	let maxDate = weekEnd.clone();

	if (isEdit) {
		const existingStartDay = toStartOfDay(existingStart);
		const existingEndDay = toStartOfDay(existingEnd);

		if (existingStartDay?.isBefore(minDate)) {
			minDate = existingStartDay;
		}
		if (existingEndDay?.isBefore(minDate)) {
			minDate = existingEndDay;
		}
		if (existingStartDay?.isAfter(maxDate)) {
			maxDate = existingStartDay.clone().endOf('day');
		}
		if (existingEndDay?.isAfter(maxDate)) {
			maxDate = existingEndDay.clone().endOf('day');
		}
	}

	return {
		minDate: minDate.format('YYYY-MM-DD'),
		maxDate: maxDate.format('YYYY-MM-DD'),
	};
}

/** Local calendar datetime for picker values (avoids UTC day-shift from toISOString). */
export function toLocalDateTimeValue(value: Date | string): string {
	const parsed = moment(value);
	return parsed.isValid() ? parsed.format('YYYY-MM-DDTHH:mm:ss') : '';
}
